/**
 * The covering person's reminder (owner, 20 Oct 2026: "i want sunil to
 * transfer vehicles in factory today till the time amit is back"): whose work
 * they hold, where, and until when — on every page while the cover lasts.
 * Outside the covered book it opens that book; inside it, it says the work
 * there is that person's, and where one's own is.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { UserRoundCheck } from 'lucide-react';
import { BOOK_NAMES, coverUntilWords } from '@bright/shared';

import { useAuth } from '@/lib/auth';
import { BOOK_LABEL, bookKeyFor, liveCover, setActiveBook, type BookKey } from '@/lib/book';
import { useCoverInUse } from '@/lib/cover';
import { formatDateTime } from '@/lib/format';

export function CoverNudge() {
  const { user } = useAuth();
  const inUse = useCoverInUse();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // A cover past its time says nothing more, before the next session read drops it (review, 20 Oct 2026).
  const cover = liveCover(user?.cover);
  if (!user || !cover) return null;

  const where = BOOK_NAMES[cover.organizationId] ?? 'their book';
  const until = coverUntilWords(cover, cover.forUser.name, formatDateTime);
  const home = bookKeyFor(user.organization?.id ?? user.organizationId);
  const open = (book: BookKey | null) => {
    setActiveBook(book);
    void queryClient.invalidateQueries();
    navigate('/');
  };

  return (
    <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-line bg-surface-2 px-3 py-2 text-caption text-ink-2" role="status">
      <UserRoundCheck className="h-4 w-4 shrink-0 text-accent" aria-hidden />
      <span className="min-w-0 flex-1">
        You are covering <span className="font-medium text-ink">{cover.forUser.name}</span>&apos;s work — {cover.role.name}, in {where} — {until}.
        {inUse && cover.crossBook && home ? ` Your own work is in ${BOOK_LABEL[home]}.` : null}
      </span>
      {!inUse && cover.book ? (
        <button type="button" className="shrink-0 font-medium text-accent hover:underline" onClick={() => open(cover.book as BookKey)}>
          Open {cover.forUser.name}&apos;s work
        </button>
      ) : inUse && cover.crossBook && home ? (
        <button type="button" className="shrink-0 font-medium text-accent hover:underline" onClick={() => open(null)}>
          Back to {BOOK_LABEL[home]}
        </button>
      ) : null}
    </div>
  );
}
